import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Container,
    Header,
    Button,
    Modal,
    Form,
    Message,
    Dropdown,
    Icon,
} from 'semantic-ui-react';
import { isICCSupport } from '../auth';
import { Footer } from '../footer';
import { LoadingWrapper, ReactTable } from '../common';
import { State } from 'country-state-city';
import {
    getMarkets,
    createMarket,
    updateMarket,
    deleteMarket,
    assignMarketTeams,
    getAllTeams,
} from './marketsService';

const US_STATES = State.getStatesOfCountry('US').map((s) => ({
    key: s.isoCode,
    text: `${s.isoCode} — ${s.name}`,
    value: s.isoCode,
}));

const EMPTY_FORM = { label: '', state: '' };

const extractFieldErrors = (errData) => {
    const errors = {};
    ['label', 'state'].forEach((field) => {
        if (errData[field]) {
            errors[field] = Array.isArray(errData[field]) ? errData[field][0] : errData[field];
        }
    });
    return errors;
};

// ---------------------------------------------------------------------------
// MarketForm — extracted as a proper React component so the reconciler
// tracks it as a separate fiber rather than opaque JSX from a render function.
// ---------------------------------------------------------------------------
const MarketForm = ({
    formData,
    formErrors,
    modalError,
    onChange,
    showTeams,
    selectedTeamIds,
    onTeamsChange,
    allTeams,
    teamsLoading,
}) => {
    const teamOptions = useMemo(
        () => allTeams.map((t) => ({ key: t.id, text: t.name, value: t.id })),
        [allTeams]
    );

    return (
        <Form>
            {modalError && (
                <Message negative>
                    <Message.Content>{modalError}</Message.Content>
                </Message>
            )}
            <Form.Input
                label="Label"
                placeholder="e.g. Spokane"
                value={formData.label}
                error={formErrors.label ? { content: formErrors.label } : false}
                onChange={(_, { value }) => onChange('label', value)}
            />
            <Form.Field error={!!formErrors.state}>
                <label>State</label>
                <Dropdown
                    placeholder="Select state"
                    fluid
                    selection
                    search
                    options={US_STATES}
                    value={formData.state}
                    onChange={(_, { value }) => onChange('state', value)}
                />
                {formErrors.state && (
                    <div className="ui pointing red basic label">{formErrors.state}</div>
                )}
            </Form.Field>
            {showTeams && (
                <Form.Field>
                    <label>Assigned Teams</label>
                    <Dropdown
                        placeholder="Search and select teams..."
                        fluid
                        multiple
                        selection
                        search
                        loading={teamsLoading}
                        options={teamOptions}
                        value={selectedTeamIds}
                        onChange={(_, { value }) => onTeamsChange(value)}
                        noResultsMessage="No teams available"
                    />
                </Form.Field>
            )}
        </Form>
    );
};

export const Markets = () => {
    const canEdit = isICCSupport();

    const [markets, setMarkets] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [createOpen, setCreateOpen] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editMarket, setEditMarket] = useState(null);
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [marketToDelete, setMarketToDelete] = useState(null);

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState('');

    const loadMarkets = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const resp = await getMarkets();
            if (!resp.ok) throw new Error('Failed to load markets');
            setMarkets(await resp.json());
        } catch {
            setError('Failed to load markets. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAllTeams = useCallback(async () => {
        setTeamsLoading(true);
        try {
            const resp = await getAllTeams();
            if (!resp.ok) throw new Error('Failed to load teams');
            setAllTeams(await resp.json());
        } catch {
            // Non-fatal; the dropdown will just be empty
        } finally {
            setTeamsLoading(false);
        }
    }, []);

    const hasFetchedRef = useRef(false);
    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        loadMarkets();
        loadAllTeams();
    }, [loadMarkets, loadAllTeams]);

    // ---- Shared form helpers ----

    const handleFormChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const validateForm = (data) => {
        const errors = {};
        if (!data.label.trim()) errors.label = 'Label is required';
        if (!data.state) errors.state = 'State is required';
        return errors;
    };

    // ---- Create ----

    const openCreate = () => {
        setFormData(EMPTY_FORM);
        setFormErrors({});
        setModalError('');
        setCreateOpen(true);
    };

    const handleCreate = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const resp = await createMarket(formData);
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setFormErrors(extractFieldErrors(errData));
                setModalError(errData.non_field_errors?.[0] || errData.detail || '');
                return;
            }
            await loadMarkets();
            setCreateOpen(false);
        } catch {
            setModalError('Failed to create market. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // ---- Edit ----

    const closeEdit = useCallback(() => {
        setEditOpen(false);
        setEditMarket(null);
    }, []);

    const openEdit = (market) => {
        setFormData({ label: market.label, state: market.state });
        setSelectedTeamIds(allTeams.filter(t => t.market?.id === market.id).map(t => t.id));
        setFormErrors({});
        setModalError('');
        setEditMarket(market);
        setEditOpen(true);
        if (allTeams.length === 0) {
            loadAllTeams();
        }
    };

    const handleEdit = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const patchResp = await updateMarket(editMarket.id, formData);
            if (!patchResp.ok) {
                const errData = await patchResp.json().catch(() => ({}));
                setFormErrors(extractFieldErrors(errData));
                setModalError(errData.non_field_errors?.[0] || errData.detail || '');
                return;
            }

            const teamsResp = await assignMarketTeams(editMarket.id, selectedTeamIds);
            if (!teamsResp.ok) {
                const errData = await teamsResp.json().catch(() => ({}));
                setModalError(errData.detail || 'Failed to update team assignments.');
                return;
            }

            await loadMarkets();
            setEditOpen(false);
            setEditMarket(null);
        } catch {
            setModalError('Failed to update market. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // ---- Delete ----

    const openDelete = (market) => {
        setMarketToDelete(market);
        setDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!marketToDelete) return;
        setSaving(true);
        try {
            const resp = await deleteMarket(marketToDelete.id);
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setError(errData.detail || 'Failed to delete market.');
                setDeleteOpen(false);
                return;
            }
            await loadMarkets();
            setDeleteOpen(false);
            setMarketToDelete(null);
        } catch {
            setError('Failed to delete market. Please try again.');
            setDeleteOpen(false);
        } finally {
            setSaving(false);
        }
    };

    // ---- Table columns ----

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Market',
                accessor: 'label',
                Cell: ({ value }) => <strong>{value}</strong>,
            },
            {
                Header: 'State',
                accessor: 'state',
            },
            {
                Header: 'Teams',
                id: 'teams',
                accessor: (market) => allTeams.filter((t) => t.market?.id === market.id).length,
                Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value}</div>,
            },
        ];

        if (canEdit) {
            cols.push({
                Header: 'Actions',
                id: 'actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="small"
                            icon="edit"
                            aria-label={`Edit ${row.original.label}`}
                            title="Edit market"
                            onClick={() => openEdit(row.original)}
                        />
                        <Button
                            size="small"
                            icon="trash"
                            color="red"
                            aria-label={`Delete ${row.original.label}`}
                            title="Delete market"
                            onClick={() => openDelete(row.original)}
                        />
                    </div>
                ),
            });
        }

        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allTeams, canEdit]);

    // ---- Render ----

    return (
        <Container>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    marginTop: '1rem',
                }}
            >
                <Header as="h2" style={{ margin: 0 }}>Markets</Header>
                {canEdit && (
                    <Button primary icon labelPosition="left" onClick={openCreate}>
                        <Icon name="plus" />
                        Add Market
                    </Button>
                )}
            </div>

            {error && (
                <Message negative onDismiss={() => setError('')}>
                    <Message.Content>{error}</Message.Content>
                </Message>
            )}

            <LoadingWrapper loading={loading} text="Loading markets...">
                {markets.length === 0 ? (
                    <Message info content="No markets found." />
                ) : (
                    <ReactTable
                        className="markets-table react-table-mobile-compact"
                        data={markets}
                        columns={columns}
                        hideGlobalFilter
                    />
                )}
            </LoadingWrapper>

            {/* Create Modal */}
            <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="small">
                <Modal.Header>Add Market</Modal.Header>
                <Modal.Content>
                    <MarketForm
                        formData={formData}
                        formErrors={formErrors}
                        modalError={modalError}
                        onChange={handleFormChange}
                        showTeams={false}
                        selectedTeamIds={selectedTeamIds}
                        onTeamsChange={setSelectedTeamIds}
                        allTeams={allTeams}
                        teamsLoading={teamsLoading}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setCreateOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button primary onClick={handleCreate} loading={saving} disabled={saving}>
                        Create
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Edit Modal */}
            <Modal open={editOpen} onClose={closeEdit} size="small">
                <Modal.Header>Edit Market — {editMarket?.label}</Modal.Header>
                <Modal.Content>
                    <MarketForm
                        formData={formData}
                        formErrors={formErrors}
                        modalError={modalError}
                        onChange={handleFormChange}
                        showTeams
                        selectedTeamIds={selectedTeamIds}
                        onTeamsChange={setSelectedTeamIds}
                        allTeams={allTeams}
                        teamsLoading={teamsLoading}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeEdit} disabled={saving}>
                        Cancel
                    </Button>
                    <Button primary onClick={handleEdit} loading={saving} disabled={saving}>
                        Save
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} size="mini">
                <Modal.Header>Delete Market</Modal.Header>
                <Modal.Content>
                    <p>
                        Are you sure you want to delete{' '}
                        <strong>{marketToDelete?.label}</strong>?
                    </p>
                    <Message warning>
                        <Message.Header>Note</Message.Header>
                        <p>
                            Deleting this market will <strong>not</strong> automatically
                            clear the market field on currently assigned teams.
                        </p>
                    </Message>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button negative onClick={handleDelete} loading={saving} disabled={saving}>
                        Delete
                    </Button>
                </Modal.Actions>
            </Modal>

            <Footer />
        </Container>
    );
};
